<template>
    <vx-table-row :class="{ 'bg-sky-50! mb-4': props.isDefault }">
        <vx-table-row-title>
            {{ props.modelValue.name }}
        </vx-table-row-title>

        <vx-table-row-item data-test="group-targets">
            <template v-slot:label> Targets: </template>
            <template v-if="props.isDefault">All OUs and Groups</template>
            <font-awesome-icon
                v-else-if="groupCounts[props.modelValue.id!] === undefined"
                widthAuto
                :icon="faLoader"
                spin
            ></font-awesome-icon>
            <template v-else>{{ groupCounts[props.modelValue.id!] }}</template>
        </vx-table-row-item>

        <vx-table-row-buttons v-if="isAdminOrOwner">
            <vx-button
                v-if="props.readonly"
                :href="
                    props.isDefault
                        ? `/settings/teams/${props.modelValue.team_id}/message-standards/default`
                        : `/settings/teams/${props.modelValue.team_id}/message-standards/${props.modelValue.id}`
                "
                data-test="go-to-standard"
                outline
                color="muted"
            >
                Go to standard
                <font-awesome-icon widthAuto :icon="faSquareArrowUpRight" />
            </vx-button>

            <template v-else>
                <vx-dropdown placement="right">
                    <vx-button data-test="dropdown" type="button" color="muted" outline class="w-[45px]">
                        <font-awesome-icon widthAuto :icon="faEllipsis" />
                    </vx-button>

                    <template v-slot:items>
                        <vx-dropdown-item data-test="duplicate" @click="duplicateMessageStandard()">
                            Duplicate
                            <font-awesome-icon widthAuto :icon="faCopy"></font-awesome-icon>
                        </vx-dropdown-item>
                        <vx-popover class="w-full" hover :arrow="true" placement="left">
                            <vx-dropdown-item
                                data-test="delete"
                                @click="deleteMessageStandard()"
                                :disabled="props.isDefault"
                            >
                                Delete
                                <font-awesome-icon widthAuto :icon="faTrash"></font-awesome-icon>
                            </vx-dropdown-item>
                            <template v-if="props.isDefault" v-slot:content>
                                <div class="font-normal w-40">Defaults cannot be removed</div>
                            </template>
                        </vx-popover>
                    </template>
                </vx-dropdown>

                <vx-button
                    :href="
                        props.isDefault
                            ? `/settings/teams/${props.modelValue.team_id}/message-standards/default`
                            : `/settings/teams/${props.modelValue.team_id}/message-standards/${props.modelValue.id}`
                    "
                    data-test="edit"
                    outline
                    color="muted"
                >
                    Edit <font-awesome-icon widthAuto :icon="faFilePen" />
                </vx-button>
            </template>
        </vx-table-row-buttons>
    </vx-table-row>
</template>

<script setup lang="ts">
import {
    faEllipsis,
    faFilePen,
    faTrash,
    faCopy,
    faLoader,
    faSquareArrowUpRight,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxButton,
    VxDropdown,
    VxDropdownItem,
    VxPopover,
    VxSwal,
    VxTableRow,
    VxTableRowButtons,
    VxTableRowItem,
    VxTableRowTitle,
} from '@voxie/frontend-components';
import { messageStandardsClient } from '~/services';
import { catchRequestError, throwSwalRequestError } from '~/utils/request';
import type { MessageStandardType } from '~/services/messageStandards/messageStandardsClient.types';
import { useCallout } from '~/composables/useCallout';
import dayjs from '~/utils/dayjs';
import { useToasted } from '~/composables/useToasted';
import { readMessageStandard } from '~/components/settings/teams/message-standards/form/useMessageStandardDetails';
import { useMessageStandardsGroupsCount } from '~/components/settings/teams/message-standards/list/useMessageStandardsGroupsCount';

const toasted = useToasted();

const isAdminOrOwner = Spark.isAdminOrOwner;
const callout = useCallout();
const { groupCounts } = useMessageStandardsGroupsCount();

const props = defineProps<{
    modelValue: MessageStandardType;
    isDefault?: boolean;
    readonly?: boolean;
}>();

const emit = defineEmits<{
    deleted: [itemId: number];
}>();

const deleteMessageStandard = () => {
    VxSwal.confirmation('Delete Brand Standard', 'Are you sure? There is no undo.', 'danger', () =>
        messageStandardsClient
            .deleteMessageStandard({ teamId: props.modelValue.team_id! }, props.modelValue.id!)
            .then(() => emit('deleted', props.modelValue.id!))
            .catch(throwSwalRequestError)
    );
};

const duplicateMessageStandard = async () => {
    try {
        let standardToDuplicate = props.modelValue;

        if (props.isDefault) {
            const response = await messageStandardsClient.getDefaultMessageStandard({
                teamId: props.modelValue.team_id!,
            });
            standardToDuplicate = response.data;
        }

        const duplicated = {
            ...readMessageStandard(standardToDuplicate),
            name: `${standardToDuplicate.name} - copy`,
            id: undefined,
        };

        if (duplicated.transactional_config !== null) {
            duplicated.transactional_rules = undefined;
        }

        const response = await messageStandardsClient.createMessageStandard(
            { teamId: props.modelValue.team_id! },
            duplicated
        );

        callout.success(
            'form-submission',
            '<strong>New Brand Standard</strong> created successfully. Add targets below.',
            {
                ttl: dayjs().add(30, 'seconds'),
                action: {
                    label: 'Back to Standards',
                    href: `/settings/teams/${props.modelValue.team_id}#/message-standards`,
                },
                shadow: true,
            }
        );
        window.location.assign(
            `/settings/teams/${props.modelValue.team_id}/message-standards/${response.data.id}/targets`
        );
    } catch (error) {
        toasted.error(catchRequestError(error));
    }
};
</script>
